import { useState } from "react";
import { Download, AlertCircle } from "lucide-react";
import { apiCall } from "@/lib/api";
import { generateLearnerReportPdf, downloadBlob, slugForFile, type LearnerReportData } from "@/lib/simPdf";

interface Props {
  attemptId: string;
  /** Pre-known title for the filename. Optional — the report data carries it too. */
  simulationTitle?: string;
  /** Override the visible label. Defaults to "Download report". */
  label?: string;
  /** Compact pill (icon + short label). */
  compact?: boolean;
  /** Even tighter — square icon-only button (~32px), no label. */
  iconOnly?: boolean;
  /** Extra Tailwind classes for the outer button. */
  className?: string;
}

/**
 * DownloadCoachingPdfButton — one click → branded learner report PDF.
 *
 * Fetches the full per-attempt payload from /api/sim/myReport (sim, learner,
 * per-decision transcript with answers + scores + AI feedback, reflection,
 * persisted coaching), runs it through generateLearnerReportPdf() in
 * src/lib/simPdf.ts (jsPDF + branded NeuSim chrome), and triggers a synthetic
 * <a download> click. No double-click, no intermediate "Click to save PDF"
 * link.
 *
 * Surfaced today on:
 *   - SimulationSelectPage sim card (compact icon) for any completed attempt
 *   - ResultsPage end-of-attempt footer (full button)
 *
 * Previously used <CoachingPdf/> with @react-pdf — the v3 branded report
 * replaces that with the jsPDF-based generator ported from NeuArena's
 * simPdf.ts pattern. Same one-click UX; richer + branded output.
 */
export function DownloadCoachingPdfButton({ attemptId, simulationTitle, label, compact, iconOnly, className }: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await apiCall<LearnerReportData>("/sim/myReport", {
        method: "POST",
        body: { attemptId },
        timeoutMs: 60_000,
      });
      const blob = await generateLearnerReportPdf(data);
      const titleSlug = slugForFile(data.sim.title ?? simulationTitle ?? "Simulation");
      const nameSlug = slugForFile(data.participant.fullName ?? "Learner");
      downloadBlob(blob, `NeuSim-Report-${titleSlug}-${nameSlug}.pdf`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't generate the PDF");
    } finally {
      setBusy(false);
    }
  };

  if (iconOnly === true) {
    return (
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        title={label ?? "Download report"}
        aria-label={label ?? "Download report"}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-500/10 text-violet-200 transition-colors hover:border-violet-400/60 hover:bg-violet-500/15 disabled:cursor-not-allowed disabled:opacity-50 ${className ?? ""}`}
      >
        <Download className="h-3.5 w-3.5" />
      </button>
    );
  }

  if (compact === true) {
    return (
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        title={label ?? "Download report"}
        aria-label={label ?? "Download report"}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-violet-400/30 bg-violet-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-violet-200 transition-colors hover:border-violet-400/60 hover:bg-violet-500/15 disabled:cursor-not-allowed disabled:opacity-50 ${className ?? ""}`}
      >
        <Download className="h-3 w-3" /> {busy ? "Preparing…" : "Report"}
      </button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        className={`inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 ${className ?? ""}`}
      >
        <Download className="h-4 w-4" /> {busy ? "Preparing PDF…" : (label ?? "Download report")}
      </button>
      {error !== null && (
        <span className="inline-flex items-center gap-1 text-[11px] text-rose-300">
          <AlertCircle className="h-3 w-3" /> {error}
        </span>
      )}
    </div>
  );
}
